import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import {
  QuestionMarkCircleIcon,
  CheckCircleIcon,
  SparklesIcon,
  BoltIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

const HelpPageNew = () => {
  const steps = [
    {
      number: 1,
      icon: '🔐',
      title: 'ورود با حساب باسلام',
      description: 'روی دکمه "ادامه با باسلام" کلیک کنید و با حساب غرفه‌دار باسلام خود وارد شوید.',
      tip: 'محصولات شما خودکار همگام‌سازی می‌شود',
      tipColor: 'bg-blue-50 text-blue-800 border-blue-200'
    },
    {
      number: 2,
      icon: '📦',
      title: 'انتخاب محصول',
      description: 'از منوی "محصولات" روی هر محصول کلیک کنید.',
      tip: 'می‌توانید از جستجو برای پیدا کردن سریع استفاده کنید',
      tipColor: 'bg-purple-50 text-purple-800 border-purple-200'
    },
    {
      number: 3,
      icon: '✨',
      title: 'افزودن رقبا (خودکار)',
      description: 'سیستم خودکار 20 رقیب مشابه پیدا می‌کنه. فقط تیک بزنید و "افزودن" کنید.',
      tip: 'امتیاز شباهت هر رقیب نمایش داده می‌شود (0-100%)',
      tipColor: 'bg-green-50 text-green-800 border-green-200'
    },
    {
      number: 4,
      icon: '⚙️',
      title: 'انتخاب قانون (یک کلیک)',
      description: 'یکی از 3 استراتژی رو انتخاب کنید:',
      strategies: [
        { icon: '🎯', name: 'متعادل', desc: 'اگه 10% بیشتر از میانگین شد، هشدار' },
        { icon: '⚡', name: 'رقابتی', desc: 'اگه از ارزان‌ترین رقیب بیشتر شد، هشدار' },
        { icon: '🛡️', name: 'آسوده', desc: 'اگه 20% بیشتر از میانگین شد، هشدار' }
      ],
      tipColor: 'bg-orange-50 text-orange-800 border-orange-200'
    },
    {
      number: 5,
      icon: '💬',
      title: 'دریافت هشدار در چت',
      description: 'هشدارها مستقیم به چت باسلام شما ارسال می‌شود.',
      tip: 'نیازی به تلگرام نیست - همیشه در چت آنلاین هستید',
      tipColor: 'bg-indigo-50 text-indigo-800 border-indigo-200'
    }
  ];

  const features = [
    {
      icon: <BoltIcon className="h-8 w-8" />,
      emoji: '⚡',
      title: 'تنظیم یکجا',
      description: 'با یک کلیک برای همه محصولات رقبا پیدا کنید و قانون اعمال کنید.',
      items: [
        'انتخاب همه یا یک دسته',
        'خودکار پیدا کردن رقبا',
        'اعمال قانون یکسان'
      ],
      color: 'from-purple-500 to-blue-500'
    },
    {
      icon: <ChartBarIcon className="h-8 w-8" />,
      emoji: '📊',
      title: 'داشبورد هوشمند',
      description: 'وضعیت همه محصولات را در یک نگاه ببینید.',
      items: [
        'محصولات با قیمت مناسب',
        'محصولات با هشدار',
        'نمودار موقعیت قیمت'
      ],
      color: 'from-green-500 to-teal-500'
    },
    {
      icon: <SparklesIcon className="h-8 w-8" />,
      emoji: '✨',
      title: 'پیشنهاد هوشمند رقبا',
      description: 'AI محصولات مشابه را با امتیازدهی پیدا می‌کند.',
      items: [
        'بررسی عنوان، وزن، دسته',
        'امتیاز شباهت 0-100%',
        'رتبه‌بندی خودکار'
      ],
      color: 'from-yellow-500 to-orange-500'
    },
    {
      icon: <ChatBubbleLeftRightIcon className="h-8 w-8" />,
      emoji: '💬',
      title: 'نوتیفیکیشن در چت',
      description: 'خلاصه روزانه در چت باسلام شما.',
      items: [
        'یک پیام روزانه',
        '5 محصول نیاز به توجه',
        'بدون اسپم'
      ],
      color: 'from-pink-500 to-rose-500'
    }
  ];

  return (
    <div className="py-6 px-4">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 mb-4">
            <QuestionMarkCircleIcon className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-3">راهنمای استفاده</h1>
          <p className="text-lg text-muted-foreground">
            چطور از رقیب‌سنج استفاده کنیم؟
          </p>
        </div>

        <Separator className="my-8" />

        {/* گام‌های استفاده */}
        <div className="space-y-6">
          {steps.map((step) => (
            <Card key={step.number} className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4">
                {/* Step Number */}
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                    {step.number}
                  </div>
                </div>

                <div className="flex-1">
                  {/* Title */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">{step.icon}</span>
                    <h2 className="text-xl font-bold">{step.title}</h2>
                  </div>

                  {/* Description */}
                  <p className="text-muted-foreground mb-4">
                    {step.description}
                  </p>

                  {/* Strategies (for step 4) */}
                  {step.strategies && (
                    <div className="space-y-3 mb-4">
                      {step.strategies.map((strategy, idx) => (
                        <Card key={idx} className="p-3 bg-muted/50">
                          <div className="flex items-start gap-3">
                            <span className="text-2xl">{strategy.icon}</span>
                            <div>
                              <span className="font-semibold">{strategy.name}:</span>
                              <span className="text-sm text-muted-foreground ml-2">{strategy.desc}</span>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}

                  {/* Tip */}
                  {step.tip && (
                    <Card className={`p-3 border-2 ${step.tipColor}`}>
                      <div className="flex items-center gap-2">
                        <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
                        <span className="text-sm font-medium">{step.tip}</span>
                      </div>
                    </Card>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>

        <Separator className="my-8" />

        {/* ویژگی‌های ویژه */}
        <div>
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-2">ویژگی‌های ویژه</h2>
            <p className="text-muted-foreground">امکانات پیشرفته رقیب‌سنج</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, idx) => (
              <Card key={idx} className="overflow-hidden hover:shadow-xl transition-all duration-300 group">
                {/* Gradient Header */}
                <div className={`bg-gradient-to-r ${feature.color} p-6 text-white`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="text-4xl">{feature.emoji}</div>
                    <h3 className="text-xl font-bold">{feature.title}</h3>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-muted-foreground mb-4">
                    {feature.description}
                  </p>

                  <ul className="space-y-2">
                    {feature.items.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <Card className="p-8 text-center bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200">
          <div className="max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">آماده شروع هستید؟</h3>
            <p className="text-muted-foreground mb-6">
              برای استفاده از همه امکانات، به داشبورد بروید و محصولات خود را مدیریت کنید
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                <Link to="/dashboard">
                  <ChartBarIcon className="h-5 w-5 ml-2" />
                  رفتن به داشبورد
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
              >
                <Link to="/products">
                  <SparklesIcon className="h-5 w-5 ml-2" />
                  مشاهده محصولات
                </Link>
              </Button>
            </div>
          </div>
        </Card>

        {/* Footer Help */}
        <div className="text-center">
          <Badge variant="outline" className="text-sm">
            نیاز به کمک بیشتر؟ با پشتیبانی باسلام تماس بگیرید
          </Badge>
        </div>
      </div>
    </div>
  );
};

export default HelpPageNew;
